/**
 * BankAccountCard - Displays a single bank account with actions
 */

import type { BankAccountPublic } from '@garden-platform/shared';
import type * as React from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';

export interface BankAccountCardProps {
  bankAccount: BankAccountPublic;
  onEdit: (bankAccount: BankAccountPublic) => void;
  onDelete: (id: string) => void;
  onSetDefault: (id: string) => void;
  isDeleting?: boolean;
  isSettingDefault?: boolean;
}

export const BankAccountCard: React.FC<BankAccountCardProps> = ({
  bankAccount,
  onEdit,
  onDelete,
  onSetDefault,
  isDeleting = false,
  isSettingDefault = false,
}) => {
  const maskedAccountNumber = bankAccount.accountNumberLastFour
    ? `****${bankAccount.accountNumberLastFour}`
    : '****';

  return (
    <Card className={!bankAccount.isActive ? 'opacity-60' : undefined}>
      <CardHeader className="flex flex-row items-start justify-between pb-2">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <h4 className="font-semibold">{bankAccount.bankName}</h4>
            {bankAccount.isDefault && (
              <Badge variant="secondary" className="text-xs">
                Default
              </Badge>
            )}
            {!bankAccount.isActive && (
              <Badge variant="outline" className="text-xs">
                Inactive
              </Badge>
            )}
          </div>
          {bankAccount.accountName && (
            <p className="text-sm text-muted-foreground">{bankAccount.accountName}</p>
          )}
        </div>
        <Badge variant="outline" className="capitalize">
          {bankAccount.accountType}
        </Badge>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-muted-foreground">Account Number</p>
            <p className="font-mono">{maskedAccountNumber}</p>
          </div>
          <div>
            <p className="text-muted-foreground">Routing Number</p>
            <p className="font-mono">{bankAccount.routingNumber}</p>
          </div>
        </div>

        <div className="flex justify-end gap-2">
          {!bankAccount.isDefault && bankAccount.isActive && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onSetDefault(bankAccount.id)}
              disabled={isSettingDefault}
            >
              Set as Default
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => onEdit(bankAccount)}>
            Edit
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onDelete(bankAccount.id)}
            disabled={isDeleting || bankAccount.isDefault}
            className="text-red-600 hover:text-red-700"
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

BankAccountCard.displayName = 'BankAccountCard';
